import { notEmpty, scmpPlus } from "@product/scmp-sdk";
import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import take from "lodash/take";
import { useEffect, useRef } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import type { Disposable } from "relay-runtime";

import { accountAtom } from "shared/lib/account/atoms";

import { fetchAiEngineData } from "scmp-app/lib/ai-engine/apis";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import type {
  hooksFurtherReadingWidgetTopPicksQuery,
  hooksFurtherReadingWidgetTopPicksQuery$variables,
} from "scmp-app/queries/__generated__/hooksFurtherReadingWidgetTopPicksQuery.graphql";

export const useFurtherReadingWidgetTopPicks = (articleEntityId: string, enabled: boolean) => {
  const { user } = useAtomValue(accountAtom);
  const environment = useRelayEnvironment();
  const disposableReference = useRef<Disposable>();

  const [{ result, status }, { execute }] = useAsync(async () => {
    const {
      data: { rec_id, results },
    } = await fetchAiEngineData<AiEngineDataType.TopPicks>({
      entityId: articleEntityId,
      type: AiEngineDataType.TopPicks,
      userUuid: user?.uuid,
    });
    const entityIds = take(
      results.sort((a, b) => a.rank - b.rank).map(({ id }) => id),
      6,
    );

    const query = graphql`
      query hooksFurtherReadingWidgetTopPicksQuery(
        $entityIds: [String]
        $first: Int!
        $scmpPlusPaywallTypeIds: [String]
      ) {
        articles(
          exclude: { paywallTypeIds: $scmpPlusPaywallTypeIds }
          filter: { entityIds: $entityIds }
          first: $first
        ) {
          edges {
            node {
              entityId
              ...articleWidgetContent
            }
          }
        }
      }
    `;

    const variables: hooksFurtherReadingWidgetTopPicksQuery$variables = {
      entityIds,
      first: entityIds.length,
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
    };

    const queryRequest = getRequest(query);
    const queryDescriptor = createOperationDescriptor(queryRequest, variables);

    const data = await fetchQuery<hooksFurtherReadingWidgetTopPicksQuery>(
      environment,
      query,
      variables,
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();
    disposableReference.current = environment.retain(queryDescriptor);

    const articleMap = Object.fromEntries(
      data?.articles.edges
        .filter(({ node }) => notEmpty(node))
        .map(({ node }) => node)
        .map(node => [node.entityId, node]) ?? [],
    );

    return {
      aiRecommendationId: rec_id,
      topPicks: entityIds.map(id => articleMap[id]).filter(notEmpty),
    };
  });

  useEffect(() => {
    if (!enabled || status !== "not-executed") return;
    void execute();
    return () => {
      disposableReference.current?.dispose();
    };
  }, [enabled, execute, status]);

  return {
    aiRecommendationId: result?.aiRecommendationId ?? "",
    topPicks: result?.topPicks ?? [],
  };
};
